/**
 * NavTile — the approved home-screen navigation tile: a custom icon badge over
 * a bold label on a rounded card, with an optional count badge. Presses in on
 * tap (spring) with a haptic buzz. Replaces the old wall of identical buttons.
 */
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { IconBadge } from './IconBadge';
import { TileBadge } from './TileBadge';
import type { IconName } from './Icon';
import { buzz } from '../lib/haptics';

const MotionLink = motion.create(Link);

interface NavTileProps {
  href: string;
  icon: IconName | (string & {});
  label: string;
  /** Optional count badge (e.g. new registrations). Hidden when 0/undefined. */
  badge?: number;
  /**
   * App-icon style "N waiting" count pinned to the icon's top-right corner
   * (rose TileBadge, caps at 9+). Hidden when 0/undefined, so callers can
   * pass a count that is zeroed while its source is still loading and the
   * tile simply shows no badge rather than a wrong number.
   */
  waiting?: number;
  /**
   * `waiting` is only a floor because a check behind it failed. The pill
   * then shows "?" or "3+" instead of hiding at 0.
   */
  waitingIncomplete?: boolean;
}

export function NavTile({ href, icon, label, badge, waiting, waitingIncomplete }: NavTileProps) {
  const reduce = useReducedMotion();
  return (
    <MotionLink
      to={href}
      whileTap={reduce ? undefined : { scale: 0.97 }}
      onTapStart={() => buzz(10)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        minHeight: 104,
        padding: 'var(--space-4)',
        /* The home mockup's card language: flat white card, hairline border,
           soft ink shadow. Replaces the old silver-gloss gradient tile. */
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 18,
        boxShadow: 'var(--shadow-md)',
        textDecoration: 'none',
        color: 'var(--ink)',
        fontWeight: 800,
        fontSize: 15,
      }}
    >
      {/* Mockup icon tile: lime wash square, readable green icon (6.22:1 on
          the wash over a white card; 7.28:1 in dark - the graphical bar is
          3:1). Style override rather than an IconBadge edit, because the
          silver-gloss badge is still what Notifications and the class feed
          wear. */}
      <span style={{ position: 'relative', display: 'inline-flex', alignSelf: 'flex-start' }}>
        <IconBadge
          name={icon}
          size={42}
          radius={13}
          style={{
            background: 'var(--lime-wash)',
            border: '1px solid transparent',
            boxShadow: 'none',
            color: 'var(--lime-deep)',
          }}
        />
        {waiting != null && <TileBadge count={waiting} incomplete={waitingIncomplete === true} />}
      </span>
      <span>{label}</span>
      {badge != null && badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 'var(--space-3)',
            right: 'var(--space-3)',
            minWidth: 22,
            height: 22,
            padding: '0 7px',
            borderRadius: 999,
            background: 'linear-gradient(180deg,#7DE85C,#2E9A14)',
            color: '#06210A',
            fontSize: 12,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 10px rgba(64,209,31,0.5)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {badge}
        </span>
      )}
    </MotionLink>
  );
}

export default NavTile;
